import React, { useEffect, useState } from 'react'

import { useConfigurationStore, useFreeSpinsBonusesStore } from '@stores'
import { useFormatMoney } from '@hooks'

import spritesheetSrc from '@assets/bonus-system/gift-animation.png'
import animatedSpriteConfig from '@assets/bonus-system/gift-animation.json'
import { Translate, useTranslation } from '@amusnet/translations/react'
import {
    AnimatedSprite,
    getZoomToFit,
    BSModalBetStepper,
    BSModalOptionCard,
    isFlexiBundle,
    isSingleOptionFlexiBundle,
    cn,
} from '@amusnet/react-ui'
import { formatCurrency } from '@amusnet/amusnet-common'
import { FreeSpinsBonus } from '@amusnet/mgs-ws-client'
import { BonusType, FlexiBonusOption } from '@amusnet/bonuses-http-client'

interface FSSingleBonusProps {
    updateSelectedBonus: (bonus: FreeSpinsBonus) => void
}

const FSSingleBonus: React.FC<FSSingleBonusProps> = ({ updateSelectedBonus }: FSSingleBonusProps) => {
    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const bonuses = useFreeSpinsBonusesStore((state) => state.bonuses)
    const currency = useConfigurationStore((state) => state.currency)
    const currencyHideCents = useConfigurationStore((s) => s.currencyHideCents)
    const showFlexiIntroModal = useFreeSpinsBonusesStore((state) => state.showFlexiIntroModal)
    const fsBonus = bonuses[0]
    const selectFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectFlexiBonusOption)
    const selectedFlexiBonusOption = useFreeSpinsBonusesStore((state) => state.selectedFlexiBonusOption)

    const [betValue, setBetValue] = useState('')
    const [bundleType, setBundleType] = useState('')

    useEffect(() => {
        if (bonuses.length === 1) {
            const newBundleType = t('gameui.bonusSystem.freeSpin', undefined, Number(fsBonus.remainingAmount))

            updateSelectedBonus(fsBonus)
            setBundleType(newBundleType)
            setBetValue(formatMoney(Number(fsBonus.betValue)))
        }
    }, [bonuses, formatMoney])

    const renderFlexiIntro = () => (
        <div
            id="freespin-bonus-text"
            className="flex flex-col items-center w-full pb-4 bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
        >
            <p id="fs-single-bonus-congratulations" className="text-4xl font-bold text-white">
                {t('gameui.congratulations')}
            </p>
            <Translate
                components={{
                    title: <p id="fs-single-bonus-you-have-been-awarded" className="text-4xl font-bold text-white" />,
                    amount: (
                        <p
                            id="fs-single-bonus-amount"
                            className="text-9xl font-black text-yellow-300 leading-tight text-nowrap"
                            style={{ scale: getZoomToFit(7, formatCurrency(fsBonus.flexiBonusValue)) }}
                        />
                    ),
                    type: <p id="fs-single-bonus-type" className="text-[36px] font-bold text-white leading-snug" />,
                }}
                tKey={'gameui.bonusSystem.youHaveBeenAwarded_FLEXI'}
                placeholders={{
                    flexiBonusAmount: formatCurrency(fsBonus.flexiBonusValue),
                    currency,
                }}
            />
        </div>
    )

    const renderSelectYourBonus = () => {
        return (
            <div className="text-white text-[36px] font-bold text-center">
                {t('common.promo.bonuses.selectYourBonus_uppercase')}
            </div>
        )
    }

    const renderFlexiOptions = (currentOption: FlexiBonusOption | undefined) => {
        if (!fsBonus.flexiBonusOptions) return

        if (fsBonus.flexiBonusOptions.length > 3) {
            return (
                <div className="bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]">
                    {renderSelectYourBonus()}
                    <BSModalBetStepper
                        options={fsBonus.flexiBonusOptions}
                        selectFlexiBonusOption={selectFlexiBonusOption}
                        currency={currency}
                        bonusType={BonusType.FREE_SPIN}
                        hideCents={currencyHideCents}
                        currentOption={currentOption}
                    />
                </div>
            )
        }

        return (
            <>
                {renderSelectYourBonus()}
                <div
                    className={cn(
                        fsBonus.flexiBonusOptions.length === 2 ? 'gap-[10px] justify-center' : 'justify-evenly',
                        'flex flex-row items-center w-full pb-4 bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]'
                    )}
                >
                    {fsBonus.flexiBonusOptions.map((bonus) => (
                        <BSModalOptionCard
                            key={bonus.bonusAmount}
                            option={bonus}
                            selectedFlexiBonusOption={selectedFlexiBonusOption}
                            selectFlexiBonusOption={selectFlexiBonusOption}
                            currency={currency}
                            selectedBackground="linear-gradient(180deg, #549B04 0%, #0B2E02 100%)"
                            unselectedBackground="linear-gradient(180deg, #555 5%, #000 56.5%)"
                            showGradientLine
                            hideCents={currencyHideCents}
                        />
                    ))}
                </div>
            </>
        )
    }

    const renderFlexiBonusContent = (currentOption: FlexiBonusOption | undefined) => {
        if (showFlexiIntroModal) {
            return renderFlexiIntro()
        }

        return renderFlexiOptions(currentOption)
    }

    const renderStandardBonusContent = (isThroughFlexi: boolean) => (
        <div
            id="freespin-bonus-text"
            className="flex flex-col items-center w-full pb-4 bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
        >
            <p id="fs-single-bonus-congratulations" className="text-4xl font-bold text-white">
                {t('gameui.congratulations')}
            </p>
            <p id="fs-single-bonus-you-get" className="text-4xl font-bold text-white">
                {t('gameui.bonusSystem.youGet')}
            </p>
            <p id="fs-single-bonus-amount" className="text-9xl font-black text-yellow-300 leading-tight">
                {isThroughFlexi ? (fsBonus.flexiBonusOptions?.[0].bonusAmount ?? 0) : fsBonus.remainingAmount}
            </p>
            <p id="fs-single-bonus-type" className="text-4xl font-black text-yellow-300 leading-snug">
                {bundleType}
            </p>
            <p id="fs-single-bonus-bet-amount" className="text-4xl font-bold text-white">
                x{' '}
                {isThroughFlexi
                    ? formatMoney(fsBonus.flexiBonusOptions?.[0].betValue ?? 0, currencyHideCents)
                    : betValue}{' '}
                {currency}
            </p>
        </div>
    )

    return (
        <div id="fs-single-bonus" className="flex flex-col items-center justify-between">
            <div id="fs-single-bonus-content" className="flex flex-col w-full bg-[#19191C]">
                <AnimatedSprite
                    spritesheetSrc={spritesheetSrc}
                    config={animatedSpriteConfig}
                    duration={1.5}
                    className="m-auto"
                />

                {isFlexiBundle(fsBonus)
                    ? isSingleOptionFlexiBundle(fsBonus)
                        ? renderStandardBonusContent(true)
                        : renderFlexiBonusContent(fsBonus.flexiBonusOptions?.[0])
                    : renderStandardBonusContent(false)}
            </div>
        </div>
    )
}

export default FSSingleBonus
